/**
 * @fileoverview Main entry point for the admin dashboard
 * @description Initializes React app with providers for routing, state, and queries
 * @module main
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

// A tab left open across a deploy asks for lazy chunks that no longer exist.
// Reload to pick up the new build, but at most once per 10 seconds so a
// chunk that is genuinely missing cannot cause a reload loop.
const CHUNK_RELOAD_KEY = 'repkeep-admin:chunk-reload-at';
window.addEventListener('vite:preloadError', () => {
  try {
    const lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);
    if (Date.now() - lastReloadAt < 10_000) return;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    return;
  }
  window.location.reload();
});

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element not found');

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>
);
